// src/components/AgeVerification.tsx
import React, { useState, useEffect } from 'react';
import { X, Shield, AlertTriangle, Clock } from 'lucide-react';
import { ParentalConsentService } from '../lib/parentalConsentService';

type AgeGroup = 'under13' | 'teen' | 'adult' | null;

export interface AgeRestrictions {
  ageGroup: AgeGroup;
  birthdate: string;
  verifiedAt: string;
  canAccessMessaging: boolean;
  canAccessMusic: boolean;
  canAccessVideoRooms: boolean;
  canAccessSocialFeatures: boolean;
  requiresParentalConsent: boolean;
  parentalConsentStatus?: 'pending' | 'approved' | 'denied' | 'not_required';
  parentalConsentApproved?: boolean;
  parentalConsentDate?: string;
  accountActive: boolean; // NEW: Controls if account can be used
}

interface AgeVerificationProps {
  onVerified: (restrictions: AgeRestrictions) => void;
  onClose?: () => void;
}

export const AgeVerification: React.FC<AgeVerificationProps> = ({ onVerified, onClose }) => {
  const [birthdate, setBirthdate] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [parentName, setParentName] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState('');
  const [consentPending, setConsentPending] = useState(false);

  const calculateAge = (birthDate: string): number => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  };

  const determineAgeGroup = (age: number): AgeGroup => {
    if (age < 13) return 'under13';
    if (age >= 13 && age < 18) return 'teen';
    return 'adult';
  };

  const getRestrictions = (ageGroup: AgeGroup, birthDate: string): AgeRestrictions => {
    const base = {
      ageGroup,
      birthdate: birthDate,
      verifiedAt: new Date().toISOString(),
    };

    switch (ageGroup) {
      case 'under13':
        // COPPA compliance - very restricted + requires consent
        return {
          ...base,
          canAccessMessaging: false,
          canAccessMusic: false,
          canAccessVideoRooms: false,
          canAccessSocialFeatures: false,
          requiresParentalConsent: true,
          parentalConsentStatus: 'pending',
          accountActive: false, // BLOCKED until parent approves
        };
      
      case 'teen':
        // High school students (13-17) - moderate restrictions
        return {
          ...base,
          canAccessMessaging: true,  // Filtered/monitored
          canAccessMusic: true,      // Curated/clean only
          canAccessVideoRooms: true, // With safety features
          canAccessSocialFeatures: true, // Limited
          requiresParentalConsent: false,
          parentalConsentStatus: 'not_required',
          accountActive: true, // Active immediately
        };
      
      case 'adult':
        // College/university students (18+) - full access
        return {
          ...base,
          canAccessMessaging: true,
          canAccessMusic: true,
          canAccessVideoRooms: true,
          canAccessSocialFeatures: true,
          requiresParentalConsent: false,
          parentalConsentStatus: 'not_required',
          accountActive: true, // Active immediately
        };
      
      default:
        return {
          ...base,
          canAccessMessaging: false,
          canAccessMusic: false,
          canAccessVideoRooms: false,
          canAccessSocialFeatures: false,
          requiresParentalConsent: true,
          parentalConsentStatus: 'pending',
          accountActive: false,
        };
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!birthdate) {
      setError('Please enter your birthdate');
      return;
    }

    const age = calculateAge(birthdate);
    
    if (age < 0 || age > 120) {
      setError('Please enter a valid birthdate');
      return;
    }

    const ageGroup = determineAgeGroup(age);

    if (ageGroup === 'under13' && !parentEmail) {
      setError('Parent/guardian email is required for users under 13');
      return;
    }

    if (ageGroup === 'under13' && !parentName) {
      setError('Parent/guardian name is required');
      return;
    }

    if (!agreedToTerms) {
      setError('You must agree to the Terms of Service and Privacy Policy');
      return;
    }

    const restrictions = getRestrictions(ageGroup, birthdate);
    
    // Get current user info from localStorage
    const userStr = localStorage.getItem('whipt_user');
    const currentUser = userStr ? JSON.parse(userStr) : null;
    const childEmail = currentUser?.email || 'unknown@example.com';
    
    // For under-13, create consent request and send email
    if (ageGroup === 'under13') {
      try {
        const consent = await ParentalConsentService.createConsentRequest({
          childEmail: childEmail, // Use actual user email
          childBirthdate: birthdate,
          parentEmail: parentEmail,
          parentName: parentName,
        });

        // Save consent token for tracking
        localStorage.setItem(`consent_${consent.verification_token}`, JSON.stringify(consent));
        
        // Save restrictions with pending status (both localStorage AND database)
        localStorage.setItem('whipt_age_verification', JSON.stringify(restrictions));
        
        // Save to database for persistence across devices
        if (currentUser?.id) {
          try {
          const { turso } = await import('@/database/client');
          
          // Save age verification data
          await turso.execute({
            sql: `
              INSERT INTO user_data (id, user_id, data_type, data_key, data_value, created_at, updated_at)
              VALUES (?, ?, 'age_verification', 'restrictions', ?, datetime('now'), datetime('now'))
              `,
              args: [
                `userdata_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                currentUser.id,
                JSON.stringify(restrictions)
              ]
            });
            
            // Update user profile with birthdate and organizer type (even for minors)
            await turso.execute({
              sql: `
                UPDATE users 
                SET birthdate = ?, 
                    organizer_type = 'student',
                    updated_at = datetime('now')
                WHERE id = ?
              `,
              args: [birthdate, currentUser.id]
            });
            
            // Update localStorage user object
            const updatedUser = { ...currentUser, birthdate, organizer_type: 'student' };
            localStorage.setItem('whipt_user', JSON.stringify(updatedUser));
            
            console.log('✅ Age verification saved to database');
          } catch (dbError) {
            console.error('Failed to save age verification to database:', dbError);
            // Continue anyway - localStorage fallback
          }
        }
        
        setConsentPending(true);
        // Call onVerified to close modal and show message
        onVerified(restrictions);
      } catch (error) {
        setError('Failed to send consent email. Please try again.');
        console.error('Consent request error:', error);
      }
    } else {
      // For 13+ users, activate immediately
      localStorage.setItem('whipt_age_verification', JSON.stringify(restrictions));
      
      // Save to database for persistence AND update user profile
      if (currentUser?.id) {
        try {
          const { turso } = await import('@/database/client');
          
          // Save age verification data
          await turso.execute({
            sql: `
              INSERT INTO user_data (id, user_id, data_type, data_key, data_value, created_at, updated_at)
              VALUES (?, ?, 'age_verification', 'restrictions', ?, datetime('now'), datetime('now'))
            `,
            args: [
              `userdata_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              currentUser.id,
              JSON.stringify(restrictions)
            ]
          });
          
          // Update user profile with birthdate and organizer type
          await turso.execute({
            sql: `
              UPDATE users 
              SET birthdate = ?, 
                  organizer_type = 'student',
                  updated_at = datetime('now')
              WHERE id = ?
            `,
            args: [birthdate, currentUser.id]
          });
          
          // Update localStorage user object
          const updatedUser = { ...currentUser, birthdate, organizer_type: 'student' };
          localStorage.setItem('whipt_user', JSON.stringify(updatedUser));
          
          console.log('✅ Age verification and user profile updated');
        } catch (dbError) {
          console.error('Failed to save age verification to database:', dbError);
          // Continue anyway - localStorage fallback
        }
      }
      
      onVerified(restrictions);
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-[#1a0f2e] to-[#0a0118] border-2 border-purple-400/30 rounded-2xl w-full max-w-md p-6">
        {consentPending ? (
          /* Consent Pending Screen */
          <div className="text-center space-y-4">
            <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto">
              <Clock className="w-10 h-10 text-amber-400" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">Parental Consent Required</h2>
              <p className="text-slate-300 text-sm">Your account is pending parent/guardian approval</p>
            </div>
            
            <div className="rounded-lg bg-blue-900/20 border border-blue-400/30 p-4 text-left">
              <p className="text-sm font-semibold text-blue-200 mb-2">✅ What Happens Next:</p>
              <ol className="text-xs text-blue-300 space-y-2 list-decimal list-inside">
                <li>We've sent an email to <strong>{parentEmail}</strong></li>
                <li>Your parent/guardian will receive a verification link</li>
                <li>They'll review what data we collect and how we protect you</li>
                <li>Once they approve, your account will be activated</li>
                <li>You'll receive an email when your account is ready</li>
              </ol>
            </div>

            <div className="rounded-lg bg-amber-900/20 border border-amber-400/30 p-4 text-left">
              <p className="text-sm font-semibold text-amber-200 mb-2">⏰ Timeline:</p>
              <ul className="text-xs text-amber-300 space-y-1">
                <li>• Verification link expires in <strong>7 days</strong></li>
                <li>• Check your inbox and spam folder</li>
                <li>• Your account will be activated within minutes of approval</li>
              </ul>
            </div>

            <div className="rounded-lg bg-green-900/20 border border-green-400/30 p-4 text-left">
              <p className="text-sm font-semibold text-green-200 mb-2">🔒 Your Privacy:</p>
              <ul className="text-xs text-green-300 space-y-1">
                <li>• Your data is protected by COPPA & FERPA</li>
                <li>• No social features or messaging until age 13</li>
                <li>• Parents can review and delete data anytime</li>
                <li>• We never sell or share your information</li>
              </ul>
            </div>

            <button
              onClick={onClose}
              className="w-full px-6 py-3 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-semibold transition-all"
            >
              Got It
            </button>

            <p className="text-xs text-slate-500">
              Questions? Contact <a href="mailto:support@whipt.app" className="text-purple-400 hover:underline">support@whipt.app</a>
            </p>
          </div>
        ) : (
          /* Age Verification Form */
          <>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
              <Shield className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Age Verification</h2>
              <p className="text-sm text-slate-400">Required for safety & compliance</p>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors text-white"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Birthdate Input */}
          <div>
            <label className="block text-sm font-semibold text-purple-200 mb-2">
              Your Birthdate
            </label>
            <input
              type="date"
              value={birthdate}
              onChange={(e) => setBirthdate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="w-full px-4 py-3 rounded-lg bg-black/30 border border-white/10 text-white focus:border-purple-400 focus:outline-none"
              required
            />
          </div>

          {/* Show parent email field if they might be under 13 */}
          {birthdate && calculateAge(birthdate) < 13 && (
            <div className="rounded-lg bg-amber-900/20 border border-amber-400/30 p-4 space-y-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-200">Parental Consent Required</p>
                  <p className="text-xs text-amber-300 mt-1">
                    Users under 13 require parent/guardian permission (COPPA compliance)
                  </p>
                </div>
              </div>
              <input
                type="text"
                value={parentName}
                onChange={(e) => setParentName(e.target.value)}
                placeholder="Parent/Guardian Full Name"
                className="w-full px-4 py-2 rounded-lg bg-black/30 border border-white/10 text-white placeholder-slate-500 focus:border-amber-400 focus:outline-none"
                required
              />
              <input
                type="email"
                value={parentEmail}
                onChange={(e) => setParentEmail(e.target.value)}
                placeholder="parent@example.com"
                className="w-full px-4 py-2 rounded-lg bg-black/30 border border-white/10 text-white placeholder-slate-500 focus:border-amber-400 focus:outline-none"
                required
              />
              <p className="text-xs text-amber-300">
                📧 We'll send a verification email to your parent/guardian. Your account will be activated once they approve.
              </p>
            </div>
          )}

          {/* Age-appropriate features preview */}
          {birthdate && calculateAge(birthdate) >= 0 && (
            <div className="rounded-lg bg-blue-900/20 border border-blue-400/30 p-4">
              <p className="text-sm font-semibold text-blue-200 mb-2">
                Your Access Level: {
                  determineAgeGroup(calculateAge(birthdate)) === 'adult' ? '🎓 College/University' :
                  determineAgeGroup(calculateAge(birthdate)) === 'teen' ? '📚 High School' :
                  '🧒 Parent-Supervised'
                }
              </p>
              <div className="space-y-1 text-xs text-blue-300">
                {getRestrictions(determineAgeGroup(calculateAge(birthdate)), birthdate).canAccessMessaging && (
                  <p>✅ Messaging (age-appropriate)</p>
                )}
                {getRestrictions(determineAgeGroup(calculateAge(birthdate)), birthdate).canAccessMusic && (
                  <p>✅ Music Player (curated content)</p>
                )}
                {getRestrictions(determineAgeGroup(calculateAge(birthdate)), birthdate).canAccessVideoRooms && (
                  <p>✅ Video Study Rooms (safe mode)</p>
                )}
                {!getRestrictions(determineAgeGroup(calculateAge(birthdate)), birthdate).canAccessMessaging && (
                  <p>❌ Messaging (requires 13+)</p>
                )}
              </div>
            </div>
          )}

          {/* Terms Agreement */}
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="mt-1 w-4 h-4 rounded border-white/20 bg-black/30 text-purple-500 focus:ring-purple-500"
            />
            <span className="text-xs text-slate-300">
              I agree to the{' '}
              <a href="/terms" target="_blank" className="text-purple-400 hover:underline">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/privacy" target="_blank" className="text-purple-400 hover:underline">
                Privacy Policy
              </a>
            </span>
          </label>

          {/* Error Message */}
          {error && (
            <div className="rounded-lg bg-red-900/20 border border-red-400/30 p-3">
              <p className="text-sm text-red-300">{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full px-6 py-3 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-semibold transition-all"
          >
            Verify & Continue
          </button>
        </form>

        <p className="text-xs text-slate-500 text-center mt-4">
          We take student safety seriously. Age verification helps us provide appropriate content and features.
        </p>
          </>
        )}
      </div>
    </div>
  );
};

// Hook to check age restrictions
export const useAgeRestrictions = (): AgeRestrictions | null => {
  const [restrictions, setRestrictions] = useState<AgeRestrictions | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem('whipt_age_verification');
    if (stored) {
      try {
        setRestrictions(JSON.parse(stored));
      } catch {
        setRestrictions(null);
      }
    }
  }, []);

  return restrictions;
};

// Feature gate component
interface FeatureGateProps {
  feature: 'messaging' | 'music' | 'videoRooms' | 'social';
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export const AgeRestrictedFeature: React.FC<FeatureGateProps> = ({ 
  feature, 
  children, 
  fallback 
}) => {
  const restrictions = useAgeRestrictions();

  if (!restrictions) {
    return fallback || (
      <div className="rounded-lg bg-amber-900/20 border border-amber-400/30 p-4 text-center">
        <Shield className="w-8 h-8 text-amber-400 mx-auto mb-2" />
        <p className="text-sm text-amber-200 font-semibold">Age Verification Required</p>
        <p className="text-xs text-amber-300 mt-1">Please verify your age to access this feature</p>
      </div>
    );
  }

  const canAccess = {
    messaging: restrictions.canAccessMessaging,
    music: restrictions.canAccessMusic,
    videoRooms: restrictions.canAccessVideoRooms,
    social: restrictions.canAccessSocialFeatures,
  }[feature];

  if (!canAccess) {
    const ageRequirement = feature === 'messaging' || feature === 'social' ? '13+' : '13+';
    return fallback || (
      <div className="rounded-lg bg-red-900/20 border border-red-400/30 p-4 text-center">
        <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
        <p className="text-sm text-red-200 font-semibold">Age Restricted Feature</p>
        <p className="text-xs text-red-300 mt-1">
          This feature requires users to be {ageRequirement}
        </p>
        <p className="text-xs text-red-400 mt-2">
          For your safety, this feature is not available for your age group.
        </p>
      </div>
    );
  }

  return <>{children}</>;
};

export default AgeVerification;
